import React, { useState } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Plane, X, Sparkles } from "lucide-react";

export const TOURISM_DISTRICT_SLUGS = ["wayanad", "idukki", "ernakulam"];
export const TRAVEL_CATEGORY_SLUGS = ["hotels_resorts", "travel_tours"];

export const INCLUSION_PRESETS = [
    "Free Breakfast",
    "Airport Pickup",
    "WiFi",
    "AC Vehicle",
    "Guided Tour",
    "Full Board Meals",
    "Pickup & Drop",
    "Sightseeing Included",
];

const MAX_INCLUSIONS = 15;
const MAX_INCLUSION_LEN = 80;

/**
 * Travel & Booking Details editor.
 *
 * Controlled: pass `value` (`{inclusions, booking_cta_type, booking_whatsapp,
 * booking_phone, booking_price_label}`) + `onChange(partialValue)`.
 */
export default function TravelBookingSection({ value = {}, onChange, testidPrefix = "tb" }) {
    const [chipDraft, setChipDraft] = useState("");
    const inclusions = value.inclusions || [];
    const cta = value.booking_cta_type || "NONE";
    const set = (patch) => onChange({ ...value, ...patch });

    const addInclusion = (raw) => {
        const t = (raw || "").trim();
        if (!t) return;
        if (t.length > MAX_INCLUSION_LEN) return;
        if (inclusions.length >= MAX_INCLUSIONS) return;
        if (inclusions.some((x) => x.toLowerCase() === t.toLowerCase())) return;
        set({ inclusions: [...inclusions, t] });
    };

    const removeInclusion = (idx) => {
        const next = inclusions.filter((_, i) => i !== idx);
        set({ inclusions: next });
    };

    const onChipKeyDown = (e) => {
        if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            addInclusion(chipDraft);
            setChipDraft("");
        } else if (e.key === "Backspace" && !chipDraft && inclusions.length) {
            removeInclusion(inclusions.length - 1);
        }
    };

    const setCta = (v) => {
        const next = { booking_cta_type: v === "NONE" ? null : v };
        // Clear irrelevant contact fields on switch
        if (v === "NONE") {
            next.booking_whatsapp = null;
            next.booking_phone = null;
        } else if (v === "WHATSAPP") {
            next.booking_phone = null;
        } else if (v === "CALL") {
            next.booking_whatsapp = null;
        }
        set(next);
    };

    const setNumeric10 = (key) => (e) => {
        const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 10);
        set({ [key]: digits });
    };

    return (
        <div
            className="rounded-2xl border border-emerald-100 bg-gradient-to-br from-emerald-50 to-teal-50 p-4"
            data-testid={`${testidPrefix}-section`}
        >
            <div className="mb-3 flex items-center gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-emerald-500 text-white">
                    <Plane size={14} />
                </div>
                <div>
                    <div className="text-sm font-bold text-neutral-800">
                        Travel &amp; Booking Details
                    </div>
                    <div className="text-[11px] text-neutral-500">
                        Optional — helps guests book faster.
                    </div>
                </div>
            </div>

            {/* Inclusions */}
            <div>
                <Label className="text-xs">What's included</Label>
                <div
                    className="mt-1 flex min-h-[44px] flex-wrap items-center gap-1.5 rounded-xl border border-neutral-200 bg-white p-2"
                    data-testid={`${testidPrefix}-inclusions-chips`}
                >
                    {inclusions.map((chip, i) => (
                        <Badge
                            key={`${chip}-${i}`}
                            variant="secondary"
                            className="bg-emerald-100 pl-2 pr-1 text-[11px] font-semibold text-emerald-800 hover:bg-emerald-200"
                            data-testid={`${testidPrefix}-chip-${i}`}
                        >
                            {chip}
                            <button
                                type="button"
                                onClick={() => removeInclusion(i)}
                                className="ml-1 rounded-full p-0.5 hover:bg-emerald-300"
                                data-testid={`${testidPrefix}-chip-remove-${i}`}
                                aria-label={`Remove ${chip}`}
                            >
                                <X size={12} />
                            </button>
                        </Badge>
                    ))}
                    <input
                        type="text"
                        placeholder={inclusions.length ? "" : "Type & press Enter"}
                        value={chipDraft}
                        onChange={(e) => setChipDraft(e.target.value.slice(0, MAX_INCLUSION_LEN))}
                        onKeyDown={onChipKeyDown}
                        onBlur={() => {
                            if (chipDraft) {
                                addInclusion(chipDraft);
                                setChipDraft("");
                            }
                        }}
                        maxLength={MAX_INCLUSION_LEN}
                        data-testid={`${testidPrefix}-chip-input`}
                        className="min-w-[80px] flex-1 border-0 bg-transparent p-1 text-sm outline-none placeholder:text-neutral-400"
                    />
                </div>
                <div className="mt-1 text-[10px] text-neutral-500">
                    {inclusions.length}/{MAX_INCLUSIONS} · press Enter or comma to add
                </div>

                {inclusions.length < MAX_INCLUSIONS && (
                    <div className="mt-2 flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-presets`}>
                        <Sparkles size={12} className="mt-1 text-neutral-400" />
                        {INCLUSION_PRESETS.filter(
                            (p) => !inclusions.some((x) => x.toLowerCase() === p.toLowerCase()),
                        ).map((p) => (
                            <button
                                key={p}
                                type="button"
                                onClick={() => addInclusion(p)}
                                data-testid={`${testidPrefix}-preset-${p.replace(/\W+/g, "-")}`}
                                className="rounded-full border border-neutral-300 bg-white px-2 py-0.5 text-[11px] font-medium text-neutral-700 hover:border-emerald-400 hover:bg-emerald-50 hover:text-emerald-700"
                            >
                                + {p}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* CTA */}
            <div className="mt-4">
                <Label className="text-xs">Booking contact button(s)</Label>
                <RadioGroup
                    value={cta}
                    onValueChange={setCta}
                    className="mt-1 grid grid-cols-4 gap-2"
                    data-testid={`${testidPrefix}-cta`}
                >
                    {[
                        { v: "NONE", label: "None" },
                        { v: "WHATSAPP", label: "WhatsApp" },
                        { v: "CALL", label: "Call" },
                        { v: "BOTH", label: "Both" },
                    ].map((o) => (
                        <label
                            key={o.v}
                            data-testid={`${testidPrefix}-cta-${o.v}`}
                            className={`flex cursor-pointer items-center justify-center gap-1 rounded-xl border p-2 text-[11px] font-semibold ${cta === o.v ? "border-emerald-500 bg-emerald-100 text-emerald-800" : "border-neutral-200 bg-white text-neutral-600"}`}
                        >
                            <RadioGroupItem value={o.v} id={`cta-${o.v}`} />
                            {o.label}
                        </label>
                    ))}
                </RadioGroup>
            </div>

            {/* Contact fields */}
            {(cta === "WHATSAPP" || cta === "BOTH") && (
                <div className="mt-3">
                    <Label className="text-xs">WhatsApp number</Label>
                    <div className="mt-1 flex items-center gap-2">
                        <span className="rounded-lg bg-white px-3 py-2 text-sm text-neutral-500 shadow-sm">
                            +91
                        </span>
                        <Input
                            className="bg-white"
                            placeholder="9876543210"
                            value={value.booking_whatsapp || ""}
                            inputMode="numeric"
                            maxLength={10}
                            onChange={setNumeric10("booking_whatsapp")}
                            data-testid={`${testidPrefix}-whatsapp`}
                        />
                    </div>
                </div>
            )}
            {(cta === "CALL" || cta === "BOTH") && (
                <div className="mt-3">
                    <Label className="text-xs">Phone number</Label>
                    <div className="mt-1 flex items-center gap-2">
                        <span className="rounded-lg bg-white px-3 py-2 text-sm text-neutral-500 shadow-sm">
                            +91
                        </span>
                        <Input
                            className="bg-white"
                            placeholder="9876543210"
                            value={value.booking_phone || ""}
                            inputMode="numeric"
                            maxLength={10}
                            onChange={setNumeric10("booking_phone")}
                            data-testid={`${testidPrefix}-phone`}
                        />
                    </div>
                </div>
            )}

            {/* Price label */}
            <div className="mt-3">
                <Label className="text-xs">Price label (optional)</Label>
                <Input
                    className="mt-1 bg-white"
                    placeholder="From ₹4999/night or Call for Price"
                    maxLength={50}
                    value={value.booking_price_label || ""}
                    onChange={(e) => set({ booking_price_label: e.target.value })}
                    data-testid={`${testidPrefix}-price-label`}
                />
            </div>
        </div>
    );
}
